import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import colors from 'Common/constants/colors';
import { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { AddSharp, ArrowDropDown, ArrowDropUp } from '@material-ui/icons';

const TableWrapper = styled.div``;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

export const Table = ({ wrapperRef, children, ...props }) => (
    <TableWrapper ref={wrapperRef}>
        <StyledTable {...props}>{children}</StyledTable>
    </TableWrapper>
);

export const TableHead = styled.thead``;

export const TableHeadRow = styled.tr``;

export const TableHeadCell = styled.th`
    position: sticky;
    padding: 10px 15px;
    text-align: left;
    font-weight: 600;
    color: ${colors.LIGHT_GRAY};
    background: ${colors.DARK_GRAY};
    z-index: 1;
`;

export const TableBody = styled.tbody``;

export const TableBodyCell = styled.td`
    padding: 10px 15px;
    color: ${colors.WHITE};
`;

export const TableBodyRow = styled.tr`
    ${ifProp(
        'hasHiddenColumns',
        css`
            ${TableBodyCell} {
                padding-top: 20px;
            }
        `,
        css`
            border-bottom: 1px solid ${colors.LIGHT_GRAY};
        `,
    )};

    &:last-child {
        border-bottom: 0 none;
    }
`;

export const HiddenColumnsTableBodyRow = styled(TableBodyRow)``;

export const HiddenColumnsTableBodyCell = styled(TableBodyCell)`
    padding-bottom: 20px;
`;

export const HiddenColumnProperties = styled.div`
    display: flex;
    flex-wrap: wrap;
    padding-left: 1px;
    padding-top: 1px;
`;

export const HiddenColumnProperty = styled.div`
    flex: 1 0 auto;
    min-width: 150px;
    max-width: 100%;
    margin-left: -1px;
    margin-top: -1px;
    padding: 10px 20px;
    background: ${colors.ALT_GRAY};
    border: 1px solid ${colors.GRAY};
`;

export const HiddenColumnLabel = styled.div`
    margin-bottom: 5px;
    color: ${colors.LIGHT_GRAY};
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
`;

export const HiddenColumnValue = styled.div`
    color: ${colors.LIGHTER_GRAY};
    font-weight: 600;
    font-size: 16px;
`;

const StyledCreateTableBodyRow = styled(TableBodyRow)`
    background-image: linear-gradient(
        to left,
        rgba(232, 157, 96, 0.8),
        transparent 110%
    );
    cursor: pointer;

    svg {
        font-size: 32px;
    }
`;

const CreateTableActionBodyCell = styled(TableBodyCell)`
    text-align: center;
`;

const CreateTableBodyRow = ({ columnsCount, children, ...props }) => (
    <StyledCreateTableBodyRow {...props}>
        <TableBodyCell colSpan={columnsCount - 1}>{children}</TableBodyCell>
        <CreateTableActionBodyCell>
            <AddSharp />
        </CreateTableActionBodyCell>
    </StyledCreateTableBodyRow>
);

CreateTableBodyRow.propTypes = {
    columnsCount: PropTypes.number.isRequired,
};

export { CreateTableBodyRow };

export const TableRowActions = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const StyledActionButton = styled.button`
    background: none;
    border: 0 none;
    padding: 0;
    min-width: 40px;
    height: 40px;
    outline: 0 none;
    color: ${colors.LIGHTER_GRAY};
    cursor: pointer;

    &:hover,
    &:focus {
        color: ${colors.WHITE};
        outline: 0 none;
    }

    &[disabled] {
        color: ${colors.LIGHT_GRAY};
        cursor: not-allowed;

        &:hover,
        &:focus {
            color: ${colors.LIGHT_GRAY};
        }
    }
`;

const TableRowActionButton = ({ icon: Icon, ...props }) => (
    <StyledActionButton type="button" {...props}>
        <Icon size="s" style={{ fontSize: '20px' }} />
    </StyledActionButton>
);

TableRowActionButton.propTypes = {
    icon: PropTypes.any.isRequired,
};

export { TableRowActionButton };

const SortArrowWrapper = styled.span`
    display: inline-block;
    vertical-align: middle;
`;

const SortArrow = ({ column }) => (
    <SortArrowWrapper>
        {column.isSortedDesc ? (
            <ArrowDropDown fontSize="small" />
        ) : (
            <ArrowDropUp fontSize="small" />
        )}
    </SortArrowWrapper>
);

SortArrow.propTypes = {
    column: PropTypes.object.isRequired,
};

const DefaultTableHeadCell = ({ column, before, after, ...props }) => (
    <TableHeadCell
        {...props}
        {...(column.getSortByToggleProps
            ? column.getHeaderProps(column.getSortByToggleProps())
            : column.getHeaderProps())}
    >
        {before}
        {column.render('Header')}
        {column.isSorted && <SortArrow column={column} />}
        {after}
    </TableHeadCell>
);

DefaultTableHeadCell.propTypes = {
    column: PropTypes.object.isRequired,
};

export { DefaultTableHeadCell };

const DefaultTableHeadRow = ({ headerGroup, before, after, ...props }) => (
    <TableHeadRow {...props} {...headerGroup.getHeaderGroupProps()}>
        {before}
        {headerGroup.headers.map(column => (
            <DefaultTableHeadCell
                column={column}
                {...(column.getSortByToggleProps
                    ? column.getHeaderProps(column.getSortByToggleProps())
                    : column.getHeaderProps())}
            />
        ))}
        {after}
    </TableHeadRow>
);

DefaultTableHeadRow.propTypes = {
    headerGroup: PropTypes.object.isRequired,
};

export { DefaultTableHeadRow };

const DefaultTableHead = ({ headerGroups, before, after, ...props }) => (
    <TableHead {...props}>
        {before}
        {headerGroups.map(headerGroup => (
            <DefaultTableHeadRow
                headerGroup={headerGroup}
                {...headerGroup.getHeaderGroupProps()}
            />
        ))}
        {after}
    </TableHead>
);

DefaultTableHead.propTypes = {
    headerGroups: PropTypes.array.isRequired,
};

export { DefaultTableHead };

const DefaultHiddenColumnProperties = ({
    row,
    hiddenColumns,
    before,
    after,
    ...props
}) => (
    <HiddenColumnProperties {...props}>
        {before}
        {row.allCells
            .filter(cell => hiddenColumns.indexOf(cell.column.id) !== -1)
            .map(cell => {
                if (!cell.column.Header && !cell.value) {
                    return null;
                }

                return (
                    <HiddenColumnProperty key={cell.column.id}>
                        <HiddenColumnLabel>
                            {cell.column.render('Header')}
                        </HiddenColumnLabel>
                        <HiddenColumnValue>
                            {cell.render('Cell')}
                        </HiddenColumnValue>
                    </HiddenColumnProperty>
                );
            })}
        {after}
    </HiddenColumnProperties>
);

DefaultHiddenColumnProperties.propTypes = {
    row: PropTypes.object.isRequired,
    hiddenColumns: PropTypes.array.isRequired,
};

export { DefaultHiddenColumnProperties };

const DefaultHiddenColumnsTableBodyRow = ({
    row,
    hiddenColumns,
    visibleColumns,
    before,
    after,
    ...props
}) => (
    <HiddenColumnsTableBodyRow {...props}>
        <HiddenColumnsTableBodyCell
            colSpan={visibleColumns.length > 1 ? visibleColumns.length : 2}
        >
            {before}
            <DefaultHiddenColumnProperties
                row={row}
                hiddenColumns={hiddenColumns}
            />
            {after}
        </HiddenColumnsTableBodyCell>
    </HiddenColumnsTableBodyRow>
);

DefaultHiddenColumnsTableBodyRow.propTypes = {
    row: PropTypes.object.isRequired,
    hiddenColumns: PropTypes.array.isRequired,
    visibleColumns: PropTypes.array.isRequired,
};

export { DefaultHiddenColumnsTableBodyRow };

const DefaultTableBodyCell = ({ cell, before, after, ...props }) => (
    <TableBodyCell {...props} {...cell.getCellProps()}>
        {before}
        {cell.render('Cell')}
        {after}
    </TableBodyCell>
);

DefaultTableBodyCell.propTypes = {
    cell: PropTypes.object.isRequired,
};

export { DefaultTableBodyCell };

const DefaultTableBodyRow = ({ row, before, after, ...props }) => (
    <TableBodyRow {...props} {...row.getRowProps()}>
        {before}
        {row.cells.map(cell => (
            <DefaultTableBodyCell cell={cell} {...cell.getCellProps()} />
        ))}
        {after}
    </TableBodyRow>
);

DefaultTableBodyRow.propTypes = {
    row: PropTypes.object.isRequired,
};

export { DefaultTableBodyRow };

const DefaultTableBody = ({
    rows,
    hiddenColumns,
    visibleColumns,
    getTableBodyProps,
    prepareRow,
    before,
    after,
    ...props
}) => (
    <TableBody {...props} {...getTableBodyProps()}>
        {before}
        {rows.map(row => {
            prepareRow(row);

            const hasHiddenColumns =
                !!hiddenColumns && hiddenColumns.length > 0;

            return (
                <Fragment key={`${row.getRowProps().key}_fragment`}>
                    <DefaultTableBodyRow
                        row={row}
                        hasHiddenColumns={hasHiddenColumns}
                    />
                    {hasHiddenColumns && (
                        <DefaultHiddenColumnsTableBodyRow
                            row={row}
                            hiddenColumns={hiddenColumns}
                            visibleColumns={visibleColumns}
                        />
                    )}
                </Fragment>
            );
        })}
        {after}
    </TableBody>
);

DefaultTableBody.defaultProps = {
    hiddenColumns: null,
    visibleColumns: null,
};

DefaultTableBody.propTypes = {
    rows: PropTypes.array.isRequired,
    hiddenColumns: PropTypes.array,
    visibleColumns: PropTypes.array,
    getTableBodyProps: PropTypes.func.isRequired,
    prepareRow: PropTypes.func.isRequired,
};

export { DefaultTableBody };

const DefaultTable = ({
    headerGroups,
    rows,
    hiddenColumns,
    visibleColumns,
    getTableProps,
    getTableBodyProps,
    prepareRow,
    ...props
}) => (
    <Table {...props} {...getTableProps()}>
        <DefaultTableHead headerGroups={headerGroups} />
        <DefaultTableBody
            rows={rows}
            hiddenColumns={hiddenColumns}
            visibleColumns={visibleColumns}
            getTableBodyProps={getTableBodyProps}
            prepareRow={prepareRow}
        />
    </Table>
);

DefaultTable.defaultProps = {
    hiddenColumns: null,
    visibleColumns: null,
};

DefaultTable.propTypes = {
    headerGroups: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
    hiddenColumns: PropTypes.array,
    visibleColumns: PropTypes.array,
    getTableProps: PropTypes.func.isRequired,
    getTableBodyProps: PropTypes.func.isRequired,
    prepareRow: PropTypes.func.isRequired,
};

export { DefaultTable };
